import { Rng } from '../rng';
import { SpatialHash } from '../spatial';
import { GameMap } from './map';
import { EventBuffer } from './events';
import type { AnyUnit, EntityId, Interactable, MobEnt, Pickup, PlayerEnt, Projectile, Unit, Zone } from './types';
import { stepPlayers, cleanupDeadPlayers } from './player';
import { stepMobs } from './mobs';
import { stepProjectiles, stepZones } from './projectiles';
import { tickStatuses } from './status';
import { stepPickups } from './loot';
import { RealmState, stepRealm } from './realm';
import { DuelState, stepDuel } from './duel';

export const TPS = 20;
export const DT = 1 / TPS;

export interface WorldListeners {
  onPlayerDeath?: (p: PlayerEnt, killer: AnyUnit | null) => void;
  onRankUp?: (p: PlayerEnt) => void;
  onBossKilled?: (boss: MobEnt, killer: AnyUnit | null) => void;
  onRevenantKilled?: (rev: MobEnt, killer: AnyUnit | null) => void;
  onLevelUp?: (p: PlayerEnt) => void;
}

export class World {
  readonly rng: Rng;
  tick = 0;
  readonly dt = DT;
  map: GameMap;
  players = new Map<EntityId, PlayerEnt>();
  mobs = new Map<EntityId, MobEnt>();
  projectiles = new Map<EntityId, Projectile>();
  zones = new Map<EntityId, Zone>();
  pickups = new Map<EntityId, Pickup>();
  interactables = new Map<EntityId, Interactable>();
  events = new EventBuffer();
  hash = new SpatialHash<Unit>(96);
  listeners: WorldListeners = {};
  realm: RealmState | null = null;
  duel: DuelState | null = null;
  private nextId = 1;

  constructor(
    readonly mode: 'realm' | 'duel',
    readonly seed: number,
    map?: GameMap,
  ) {
    this.rng = new Rng(seed);
    this.map = map ?? (mode === 'realm' ? GameMap.generateRealm(seed) : GameMap.generateArena(seed));
  }

  allocId(): EntityId {
    return this.nextId++;
  }

  unit(id: EntityId): AnyUnit | undefined {
    return this.players.get(id) ?? this.mobs.get(id);
  }

  seconds(s: number): number {
    return Math.max(1, Math.round(s * TPS));
  }

  /** All living units (players and mobs including summons). */
  *units(): IterableIterator<AnyUnit> {
    for (const p of this.players.values()) if (!p.dead) yield p;
    for (const m of this.mobs.values()) if (!m.dead) yield m;
  }

  rebuildHash(): void {
    this.hash.clear();
    for (const u of this.units()) this.hash.insert(u);
  }

  /** Enemies of `team` within radius, sorted by distance. Untargetable units (pets) are skipped. */
  enemiesNear(x: number, y: number, r: number, team: number): AnyUnit[] {
    const out = this.hash.queryCircle(x, y, r) as AnyUnit[];
    const res = out.filter((u) => u.team !== team && !u.dead && !u.untargetable);
    res.sort((a, b) => (a.x - x) ** 2 + (a.y - y) ** 2 - ((b.x - x) ** 2 + (b.y - y) ** 2));
    return res;
  }

  alliesNear(x: number, y: number, r: number, team: number): AnyUnit[] {
    const out = this.hash.queryCircle(x, y, r) as AnyUnit[];
    return out.filter((u) => u.team === team && !u.dead && !u.untargetable);
  }

  nearestEnemy(x: number, y: number, r: number, team: number): AnyUnit | undefined {
    return this.hash.nearest(x, y, r, (u) => u.team !== team && !(u as AnyUnit).dead && !(u as AnyUnit).untargetable) as AnyUnit | undefined;
  }

  step(): void {
    this.tick++;
    this.rebuildHash();
    for (const u of this.units()) tickStatuses(this, u);
    stepPlayers(this);
    stepMobs(this);
    stepProjectiles(this);
    stepZones(this);
    stepPickups(this);
    if (this.realm) stepRealm(this);
    if (this.duel) stepDuel(this);
    for (const [id, m] of this.mobs) if (m.dead) this.mobs.delete(id);
    cleanupDeadPlayers(this);
  }
}
